import React from "react";
import { isNull, isUndefined } from "lodash";
import { Link } from "react-router-dom";

import { OverflowTooltip } from "components";
import {
	agentPermissionSet,
	hasPermissions,
	permissionList,
	viewScheduleAOrResidualSplitsPermissionSet,
} from "helpers";
import {
	getAgentHistoryTitleName,
	getFeeFieldName,
	getPaymentMethodTypes,
	getScheduleAFeeLabel,
} from "mappers";
import {
	BusinessUnitHierarchyTypes,
	BusinessUnitTypeObsolete,
	BusinessUnitTypes,
	PayoutMethodTypes,
	PriorityTypes,
	ScenarioTypes,
} from "types";

import styles from "./agentHistory.module.scss";

function renderField(name, value, hasPermission, url) {
	const link = value && hasPermission ? url : "";
	return link ? (
		<Link target="_blank" to={link}>
			{name}
		</Link>
	) : (
		<span className="text-white">{name || "none"}</span>
	);
}

export const primaryAgentRender = ({ from, to, index }) => {
	const urlFrom = `/system-setup/agent-setup/edit-agent/${from?.value}/general-info`;
	const urlTo = `/system-setup/agent-setup/edit-agent/${to?.value}/general-info`;
	const hasPermission = hasPermissions(agentPermissionSet);

	function changesFrom() {
		return renderField(from?.name, from?.value, hasPermission, urlFrom);
	}
	function changesTo() {
		return renderField(to?.name, to?.value, hasPermission, urlTo);
	}

	return (
		<li key={index}>
			Changed
			<span className="text-white"> Agent </span>
			from {changesFrom()} to {changesTo()}
		</li>
	);
};

export const companyRender = ({ from, to, index }) => {
	const urlFrom = `/system-setup/agent-office-setup/edit-agent-office/${from?.value}/general-info`;
	const urlTo = `/system-setup/agent-office-setup/edit-agent-office/${to?.value}/general-info`;
	const hasPermission = hasPermissions(agentPermissionSet);

	function changesFrom() {
		return renderField(from?.name, from?.value, hasPermission, urlFrom);
	}
	function changesTo() {
		return renderField(to?.name, to?.value, hasPermission, urlTo);
	}

	return (
		<li key={index}>
			Changed
			<span className="text-white"> Agent Office </span>
			from {changesFrom()} to {changesTo()}
		</li>
	);
};

export const scheduleARender = ({
	from,
	to,
	index,
	scope,
	currentUserId,
	allModifications,
}) => {
	const agentIdTo = scope?.primary_agent_id?.value;

	const modifiedPrimaryAgentId =
		allModifications?.primary_agent_id?.from?.value;

	const agentIdFrom = modifiedPrimaryAgentId
		? modifiedPrimaryAgentId
		: scope?.primary_agent_id?.value;

	const isHasPrimaryAgentViewResidualsDetailsForTheirAccountsPermissionFromAgentFrom =
		currentUserId === agentIdFrom
			? hasPermissions(
					permissionList.PrimaryAgentViewResidualsDetailsForTheirAccounts,
			  )
			: true;

	const isHasPrimaryAgentViewResidualsDetailsForTheirAccountsPermissionFromAgentTo =
		currentUserId === agentIdTo
			? hasPermissions(
					permissionList.PrimaryAgentViewResidualsDetailsForTheirAccounts,
			  )
			: true;

	const urlFrom = `/system-setup/agent-setup/edit-agent/${agentIdFrom}/schedule-a/${from?.value}/view`;
	const urlTo = `/system-setup/agent-setup/edit-agent/${agentIdTo}/schedule-a/${to?.value}/view`;
	const hasPermission =
		hasPermissions(viewScheduleAOrResidualSplitsPermissionSet) &&
		hasPermissions(agentPermissionSet);

	function changesFrom() {
		return renderField(
			from?.name,
			from?.value,
			isHasPrimaryAgentViewResidualsDetailsForTheirAccountsPermissionFromAgentFrom &&
				hasPermission,
			urlFrom,
		);
	}
	function changesTo() {
		return renderField(
			to?.name,
			to?.value,
			isHasPrimaryAgentViewResidualsDetailsForTheirAccountsPermissionFromAgentTo &&
				hasPermission,
			urlTo,
		);
	}

	return (
		<li key={index}>
			Changed
			<span className="text-white"> Schedule A </span>
			from {changesFrom()} to {changesTo()}
		</li>
	);
};

export const splitProfileRender = ({
	from,
	to,
	index,
	scope,
	currentUserId,
	allModifications,
}) => {
	const agentIdTo = scope?.primary_agent_id?.value;

	const modifiedPrimaryAgentId =
		allModifications?.primary_agent_id?.from?.value;

	const agentIdFrom = modifiedPrimaryAgentId
		? modifiedPrimaryAgentId
		: scope?.primary_agent_id?.value;

	const isHasPrimaryAgentViewResidualsDetailsForTheirAccountsPermissionFromAgentFrom =
		currentUserId === agentIdFrom
			? hasPermissions(
					permissionList.PrimaryAgentViewResidualsDetailsForTheirAccounts,
			  )
			: true;

	const isHasPrimaryAgentViewResidualsDetailsForTheirAccountsPermissionFromAgentTo =
		currentUserId === agentIdTo
			? hasPermissions(
					permissionList.PrimaryAgentViewResidualsDetailsForTheirAccounts,
			  )
			: true;

	const urlFrom = `/system-setup/agent-setup/edit-agent/${agentIdFrom}/residuals-split-profiles/view/${from?.value}`;
	const urlTo = `/system-setup/agent-setup/edit-agent/${agentIdTo}/residuals-split-profiles/view/${to?.value}`;
	const hasPermission =
		hasPermissions(viewScheduleAOrResidualSplitsPermissionSet) &&
		hasPermissions(agentPermissionSet);

	function changesFrom() {
		return renderField(
			from?.name,
			from?.value,
			isHasPrimaryAgentViewResidualsDetailsForTheirAccountsPermissionFromAgentFrom &&
				hasPermission,
			urlFrom,
		);
	}
	function changesTo() {
		return renderField(
			to?.name,
			to?.value,
			isHasPrimaryAgentViewResidualsDetailsForTheirAccountsPermissionFromAgentTo &&
				hasPermission,
			urlTo,
		);
	}

	return (
		<li key={index}>
			Changed
			<span className="text-white"> Residuals Split Profile </span>
			from {changesFrom()} to {changesTo()}
		</li>
	);
};

export const processorRender = ({ ...modifications }) => {
	return (
		<li key={modifications?.index}>
			Changed
			<span className="text-white"> Processor </span>
			from{" "}
			<span className="text-white">
				{modifications?.from?.name || "none"}
			</span>{" "}
			to
			<span className="text-white">
				{" "}
				{modifications?.to?.name || "none"}{" "}
			</span>
		</li>
	);
};

export const payoutInfoType = ({ from, to, field_name, index }) => {
	const tooltipText = `Changed ${getAgentHistoryTitleName(field_name)} from ${
		PayoutMethodTypes.getByName(from?.value)?.displayName
	} to ${PayoutMethodTypes.getByName(to?.value)?.displayName}`;

	return (
		<OverflowTooltip
			onlyOnLaptop
			title={tooltipText}
			className="mb-3"
			key={index}
		>
			<span className={styles.renderStyles}>
				Changed
				<span className="text-white">
					{" "}
					{getAgentHistoryTitleName(field_name)}{" "}
				</span>
				{(from?.value && "from") || ""}{" "}
				<span className="text-white">
					{" "}
					{getPaymentMethodTypes(from?.value)}{" "}
				</span>{" "}
				{(to?.value && "to") || ""}
				<span className="text-white">
					{" "}
					{getPaymentMethodTypes(to?.value)}{" "}
				</span>
			</span>
		</OverflowTooltip>
	);
};

export const payResidualsToAgentOffice = ({ from, to, field_name, index }) => {
	const isFromDeletedPayResidualsToAgentOffice =
		from?.value === "False" ? "No" : "Yes";
	const isToDeletedPayResidualsToAgentOffice =
		to?.value === "False" ? "No" : "Yes";

	const tooltipText = `Changed ${getAgentHistoryTitleName(
		field_name,
	)} from ${isFromDeletedPayResidualsToAgentOffice} to ${isToDeletedPayResidualsToAgentOffice}`;

	return (
		<OverflowTooltip
			onlyOnLaptop
			title={tooltipText}
			className="mb-3"
			key={index}
		>
			<span className={styles.renderStyles}>
				Changed
				<span className="text-white">
					{" "}
					{getAgentHistoryTitleName(field_name)}{" "}
				</span>
				from{" "}
				<span className="text-white">
					{" "}
					{isFromDeletedPayResidualsToAgentOffice}{" "}
				</span>{" "}
				to
				<span className="text-white">
					{" "}
					{isToDeletedPayResidualsToAgentOffice}{" "}
				</span>
			</span>
		</OverflowTooltip>
	);
};

export const highRiskRender = ({ from, to, index }) => {
	const value = data => {
		if (!data?.value) {
			return "none";
		}
		return data?.value === "True" ? "Yes" : "No";
	};

	return (
		<li key={index}>
			Changed
			<span className="text-white"> High Risk Merchant </span>
			from <span className="text-white">{value(from)}</span> to
			<span className="text-white"> {value(to)} </span>
		</li>
	);
};

export const mainScheduleARender =
	title =>
	({ from, to, index }) => {
		const mappingValue = value => {
			if (title === "Schedule A Status" && value) {
				return value?.charAt(0)?.toUpperCase() + value?.slice(1);
			} else {
				return value;
			}
		};

		const tooltipText = `Changed ${title} ${
			(from?.value && "from") || ""
		}${" "} ${mappingValue(from?.value) || ""} ${
			(to?.value && "to") || ""
		} ${mappingValue(to?.value) || ""}`;

		return (
			<OverflowTooltip
				onlyOnLaptop
				title={tooltipText}
				className="mb-3"
				key={index}
			>
				<span className={styles.renderStyles}>
					Changed
					<span className="text-white"> {title} </span>
					{from?.value && "from"}{" "}
					<span className="text-white">
						{" "}
						{mappingValue(from?.value) || ""}{" "}
					</span>{" "}
					{to?.value && "to"}
					<span className="text-white">
						{" "}
						{mappingValue(to?.value) || ""}
					</span>
				</span>
			</OverflowTooltip>
		);
	};

export const priorityTypeRender = ({ ...modifications }) => {
	const mainPriorityTitle = "Changed Support Level";
	return (
		<li key={modifications?.index}>
			{modifications?.scope?.is_priority_type_manualy_changed?.value
				? `Manually ${mainPriorityTitle}`
				: `Automatically ${mainPriorityTitle}`}{" "}
			from{" "}
			<span className="text-white">
				{PriorityTypes.getByName(modifications?.from?.value)
					?.displayName || "none"}
			</span>{" "}
			to
			<span className="text-white">
				{" "}
				{PriorityTypes.getByName(modifications?.to?.value)
					?.displayName || "none"}{" "}
			</span>
		</li>
	);
};

export const scheduleAFeeRender = ({ from, to, field_name, scope, index }) => {
	const keys = field_name?.split(".");

	const propertyKey = keys[keys.length - 1];
	const displayName = getFeeFieldName(propertyKey);

	const rateOrRevenueShareValue =
		(propertyKey === "rate" || propertyKey === "revenue_share") && "%";
	const perItemFeeValue = propertyKey === "per_item_fee" && "$";

	const getFeeValue = value => {
		if (propertyKey === "pricing_method") {
			const getPricingMethodValue = displayName => {
				if (displayName === "iso_discretion") return "Iso Discretion";
				else if (displayName === "pass_through") return "Pass Through";
				else return "Absolute";
			};

			return getPricingMethodValue(value);
		} else {
			return (
				<span className="text-white">
					{" "}
					{value && perItemFeeValue}
					{value || ""}
					{value && rateOrRevenueShareValue}{" "}
				</span>
			);
		}
	};

	const tooltipText = `Changed ${getScheduleAFeeLabel(
		scope?.type?.value,
	)} ${displayName}${" "}${(from?.value && "from") || ""}${" "}${
		(from?.value && perItemFeeValue) || ""
	}${from?.value || ""}${
		(from?.value && rateOrRevenueShareValue) || ""
	}${" "}${(to?.value && "to") || ""}${" "}${
		(to?.value && perItemFeeValue) || ""
	}${to?.value || ""}${(to?.value && rateOrRevenueShareValue) || ""}`;

	return (
		<>
			{!isNull(to) && (
				<OverflowTooltip
					onlyOnLaptop
					title={tooltipText}
					className="mb-3"
					key={index}
				>
					<span className={styles.renderStyles}>
						Changed
						<span className="text-white">
							{" "}
							{`${getScheduleAFeeLabel(
								scope?.type?.value,
							)} ${displayName}`}{" "}
						</span>
						{from?.value && "from"}{" "}
						<span className="text-white">
							{" "}
							{getFeeValue(from?.value)}{" "}
						</span>{" "}
						{to?.value && "to"}
						<span className="text-white">
							{" "}
							{getFeeValue(to?.value)}{" "}
						</span>
					</span>
				</OverflowTooltip>
			)}
		</>
	);
};

export const scheduleAByNameRender =
	title =>
	({ from, to, index }) => {
		const tooltipText = `Changed ${title} ${(from?.name && "from") || ""} ${
			from?.name || ""
		} ${(to?.name && "to") || ""} ${to?.name || ""}
	`;
		return (
			<OverflowTooltip
				onlyOnLaptop
				title={tooltipText}
				className="mb-3"
				key={index}
			>
				<span className={styles.renderStyles}>
					Changed
					<span className="text-white"> {title} </span>
					{from?.name && "from"}{" "}
					<span className="text-white"> {from?.name || ""} </span>{" "}
					{to?.name && "to"}
					<span className="text-white"> {to?.name || ""} </span>
				</span>
			</OverflowTooltip>
		);
	};

export const buyRatesAreApplicableRender = ({ from, to, index }) => {
	const isFromBuyRatesAreApplicable =
		from?.value === "False" ? "Unchecked" : "Checked";
	const isToBuyRatesAreApplicable =
		to?.value === "False" ? "Unchecked" : "Checked";

	const tooltipText = `Changed Buy Rates Are Applicable checkbox ${isFromBuyRatesAreApplicable} to ${isToBuyRatesAreApplicable}`;

	return (
		<OverflowTooltip
			onlyOnLaptop
			title={tooltipText}
			className="mb-3"
			key={index}
		>
			<span className={styles.renderStyles}>
				Changed
				<span className="text-white"> Buy Rates Are Applicable </span>
				checkbox from{" "}
				<span className="text-white">
					{" "}
					{isFromBuyRatesAreApplicable}{" "}
				</span>{" "}
				to
				<span className="text-white">
					{" "}
					{isToBuyRatesAreApplicable}{" "}
				</span>
			</span>
		</OverflowTooltip>
	);
};

export const createSplitProfileRender = ({ from, to, index }) => {
	if (!from)
		return (
			<li key={index}>
				Created
				<span className="text-white"> Residual Split Profile </span>
				with name
				<span className="text-white"> {to?.name} </span>
			</li>
		);
};

export const profileNameRender = ({ ...modifications }) => {
	return (
		<li key={modifications?.index}>
			Changed
			<span className="text-white"> Profile Name </span>
			from
			<span className="text-white">
				{" "}
				{modifications?.from?.value || "none"}{" "}
			</span>
			to
			<span className="text-white"> {modifications?.to?.value} </span>
		</li>
	);
};

export const profileScenarionRender = ({ ...modifications }) => {
	return (
		<li key={modifications?.index}>
			Changed
			<span className="text-white"> Scenario </span>
			from
			<span className="text-white">
				{" "}
				{ScenarioTypes.getByName(modifications?.from?.value)
					?.displayName || "none"}{" "}
			</span>
			to
			<span className="text-white">
				{" "}
				{
					ScenarioTypes.getByName(modifications?.to?.value)
						?.displayName
				}{" "}
			</span>
		</li>
	);
};

export const profileHighRiskRender = ({ from, to, index }) => {
	const value = data => (data?.value === "True" ? "Yes" : "No");

	return (
		<li key={index}>
			Changed
			<span className="text-white"> High Risk Marker </span>
			from{" "}
			<span className="text-white">{from ? value(from) : "none"}</span> to
			<span className="text-white"> {to ? value(to) : "none"} </span>
		</li>
	);
};

export const profileStatusRender = ({ ...modifications }) => {
	return (
		<li key={modifications?.index}>
			Changed
			<span className="text-white"> Status </span>
			from
			<span className="text-white">
				{" "}
				{modifications?.from?.value === "True"
					? "Available"
					: "Unavailable"}{" "}
			</span>
			to
			<span className="text-white">
				{" "}
				{modifications?.to?.value === "True"
					? "Available"
					: "Unavailable"}{" "}
			</span>
		</li>
	);
};

export const profilePercentRender = ({
	from,
	to,
	index,
	field_name,
	scope,
}) => {
	const isSecondarySplit = field_name?.includes(
		"secondary_split_information",
	);

	function changesFor() {
		const hasPermission = hasPermissions(
			agentPermissionSet && viewScheduleAOrResidualSplitsPermissionSet,
		);

		const secondaryAgent = scope?.secondary_agent_id;

		const urlFrom = `/system-setup/agent-setup/edit-agent/${secondaryAgent?.value}/general-info`;

		return renderField(
			secondaryAgent?.name,
			secondaryAgent?.value,
			hasPermission,
			urlFrom,
		);
	}

	const operationName = from?.value < to?.value ? "Increased" : "Decreased";

	if (!from?.value) {
		return (
			<li key={index}>
				Added
				<span className="text-white"> Residuals Secondary Split </span>
				{changesFor()} with Residuals Split
				<span className="text-white">
					{" "}
					{to?.value ? `${to?.value}%` : "none"}{" "}
				</span>
			</li>
		);
	} else if (!to?.value) {
		return (
			<li key={index}>
				Removed
				<span className="text-white"> Residuals Secondary Split </span>
				with Residuals Split
				<span className="text-white">
					{" "}
					{from?.value ? `${from?.value}%` : "none"}{" "}
				</span>
			</li>
		);
	} else {
		return (
			<li key={index}>
				{operationName}
				<span className="text-white">
					{" "}
					{isSecondarySplit
						? "Residuals Secondary Split"
						: "Primary Agent Residual Split"}{" "}
				</span>
				{isSecondarySplit && <>for {changesFor()} </>}
				from
				<span className="text-white">
					{" "}
					{from?.value ? `${from?.value}%` : "none"}{" "}
				</span>
				to
				<span className="text-white">
					{" "}
					{to?.value ? `${to?.value}%` : "none"}{" "}
				</span>
			</li>
		);
	}
};

export const profileTotalRevenueShareRender = ({ ...modifications }) => {
	return (
		<li key={modifications?.index}>
			Changed
			<span className="text-white"> Total Revenue Share </span>
			from
			<span className="text-white">
				{" "}
				{modifications?.from?.value
					? `${modifications?.from?.value}%`
					: "none"}{" "}
			</span>
			to
			<span className="text-white">
				{" "}
				{modifications?.to?.value
					? `${modifications?.to?.value}%`
					: "none"}{" "}
			</span>
		</li>
	);
};

export const profileSecondarySplitInformationRender = ({
	from,
	to,
	scope,
	index,
}) => {
	function changesFor() {
		const hasPermission = hasPermissions(
			agentPermissionSet && viewScheduleAOrResidualSplitsPermissionSet,
		);

		const secondaryAgent = scope?.secondary_agent_id;

		const urlFrom = `/system-setup/agent-setup/edit-agent/${secondaryAgent?.value}/general-info`;

		return renderField(
			secondaryAgent?.name,
			secondaryAgent?.value,
			hasPermission,
			urlFrom,
		);
	}
	if (!from?.value) {
		return (
			<li key={index}>
				Added{" "}
				<span className="text-white"> Residuals Secondary Split </span>
				with{" "}
				<span className="text-white">
					{" "}
					Residuals Split {scope?.split_percent?.value}%{" "}
				</span>
			</li>
		);
	} else if (!to?.value) {
		return (
			<li key={index}>
				Removed{" "}
				<span className="text-white"> Residuals Secondary Split </span>
				with{" "}
				<span className="text-white">
					{" "}
					Residuals Split {scope?.split_percent?.value}%{" "}
				</span>
			</li>
		);
	} else {
		return (
			<li key={index}>
				Changed
				<span className="text-white"> Residuals Secondary Split </span>
				for {changesFor()} from
				<span className="text-white"> {from?.value || "none"}% </span>
				to
				<span className="text-white"> {to?.value}% </span>
			</li>
		);
	}
};

export const profileSecondarySplitScheduleARender = ({
	from,
	to,
	scope,
	index,
}) => {
	const hasPermission = hasPermissions(
		agentPermissionSet && viewScheduleAOrResidualSplitsPermissionSet,
	);

	const secondaryAgent = scope?.secondary_agent_id;

	const urlWith = `/system-setup/agent-setup/edit-agent/${secondaryAgent?.value}/general-info`;
	const urlFrom = null;
	const urlTo = `/system-setup/agent-setup/edit-agent/${secondaryAgent?.value}/schedule-a/${to?.value}/view`;

	function changesFor() {
		return renderField(
			secondaryAgent?.name,
			secondaryAgent?.value,
			hasPermission,
			urlWith,
		);
	}

	function changesFrom() {
		return renderField(from?.name, from?.value, hasPermission, urlFrom);
	}

	function changesTo() {
		return renderField(to?.name, to?.value, hasPermission, urlTo);
	}

	const value = data => (data?.value ? "Yes" : "No");

	if (!from?.value && !to?.value) {
		return null;
	}

	return (
		<>
			{!(from?.value && to?.value) && (
				<li key={index + 1}>
					Changed
					<span className="text-white">
						{" "}
						Use Secondary Agent Schedule A for Calculations{" "}
					</span>
					{secondaryAgent?.name && <>for {changesFor()} </>}
					from <span className="text-white">{value(from)}</span> to
					<span className="text-white"> {value(to)} </span>
				</li>
			)}
			{from?.value && to?.value && (
				<li key={index}>
					Changed
					<span className="text-white">
						{" "}
						Secondary Agent Schedule A for Calculations{" "}
					</span>
					{secondaryAgent?.name && <>for {changesFor()} </>}
					from <span className="text-white"> {changesFrom()} </span>
					to {changesTo()}
				</li>
			)}
		</>
	);
};

export const profileSecondarySplitPrivateRender = ({
	from,
	to,
	scope,
	index,
}) => {
	const hasPermission = hasPermissions(
		agentPermissionSet && viewScheduleAOrResidualSplitsPermissionSet,
	);

	const secondaryAgent = scope?.secondary_agent_id;

	const urlFrom = `/system-setup/agent-setup/edit-agent/${secondaryAgent?.value}/general-info`;

	function changesFor() {
		return renderField(
			secondaryAgent?.name,
			secondaryAgent?.value,
			hasPermission,
			urlFrom,
		);
	}

	const hasNoChanges =
		(from === null && to === null) || (!from?.value && !to?.value);

	const fromValue = from?.value === "True" ? "Checked" : "Unchecked";
	const toValue = to?.value === "True" ? "Checked" : "Unchecked";

	return !hasNoChanges && fromValue !== toValue ? (
		<li key={index}>
			Changed
			<span className="text-white">
				{" "}
				Residuals Secondary Split Private
			</span>{" "}
			{secondaryAgent?.name && <>for {changesFor()} </>}
			from
			<span className="text-white"> {fromValue} </span>
			to
			<span className="text-white"> {toValue} </span>
		</li>
	) : null;
};

export const mainResidualsPayoutInfo = ({ from, to, field_name, index }) => {
	const mappingValue = value => {
		if (
			(field_name === "payment_info.ach.account_type" ||
				field_name === "payment_info.domestic_wire.account_type") &&
			value
		) {
			return value?.charAt(0)?.toUpperCase() + value?.slice(1);
		} else {
			return value || "None";
		}
	};

	const tooltipText = `Changed ${getAgentHistoryTitleName(
		field_name,
	)} ${"from"} ${
		isUndefined(from?.value) ? "None" : mappingValue(from?.value) || ""
	} ${"to"} ${
		isUndefined(to?.value) ? "None" : mappingValue(to?.value) || ""
	}`;

	return (
		<OverflowTooltip
			onlyOnLaptop
			title={tooltipText}
			className="mb-3"
			key={index}
		>
			<span className={styles.renderStyles}>
				Changed
				<span className="text-white">
					{" "}
					{getAgentHistoryTitleName(field_name)}{" "}
				</span>
				from{" "}
				<span className="text-white">
					{" "}
					{isUndefined(from?.value)
						? "None"
						: mappingValue(from?.value) || ""}{" "}
				</span>{" "}
				to
				<span className="text-white">
					{" "}
					{isUndefined(to?.value)
						? "None"
						: mappingValue(to?.value) || ""}{" "}
				</span>
			</span>
		</OverflowTooltip>
	);
};

export const businessUnitPayoutInfo = ({ from, to, field_name, index }) => {
	const getDisplayName = unitType => {
		const value = unitType?.value || "";
		return (
			BusinessUnitTypes.getByName(value)?.displayName ||
			BusinessUnitTypeObsolete.getByName(value)?.displayName ||
			"none"
		);
	};

	const fromDisplayName = getDisplayName(from);
	const toDisplayName = getDisplayName(to);

	const tooltipText = `Changed ${getAgentHistoryTitleName(
		field_name,
	)} from ${fromDisplayName} to ${toDisplayName}`;

	return (
		<OverflowTooltip
			onlyOnLaptop
			title={tooltipText}
			className="mb-3"
			key={index}
		>
			<span className={styles.renderStyles}>
				Changed{" "}
				<span className="text-white">
					{getAgentHistoryTitleName(field_name)}{" "}
				</span>
				from <span className="text-white"> {fromDisplayName} </span>
				to <span className="text-white"> {toDisplayName} </span>
			</span>
		</OverflowTooltip>
	);
};

export const businessUnitHierarchyPayoutInfo = ({
	from,
	to,
	field_name,
	index,
}) => {
	const tooltipText = `Changed ${getAgentHistoryTitleName(field_name)} from ${
		BusinessUnitHierarchyTypes.getByName(from?.value || "")?.displayName ||
		"none"
	} to ${
		BusinessUnitHierarchyTypes.getByName(to?.value || "")?.displayName ||
		"none"
	}`;

	return (
		<OverflowTooltip
			onlyOnLaptop
			title={tooltipText}
			className="mb-3"
			key={index}
		>
			<span className={styles.renderStyles}>
				Changed
				<span className="text-white">
					{" "}
					{getAgentHistoryTitleName(field_name)}{" "}
				</span>
				from{" "}
				<span className="text-white">
					{" "}
					{BusinessUnitHierarchyTypes.getByName(from?.value || "")
						?.displayName || "none"}{" "}
				</span>{" "}
				to
				<span className="text-white">
					{" "}
					{BusinessUnitHierarchyTypes.getByName(to?.value || "")
						?.displayName || "none"}{" "}
				</span>
			</span>
		</OverflowTooltip>
	);
};

export const createScheduleARender = ({ from, to, index }) => {
	if (!from)
		return (
			<li key={index}>
				Created
				<span className="text-white"> Schedule A </span>
				with name
				<span className="text-white"> {to?.name} </span>
			</li>
		);
};
